import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Plus, ChevronLeft, ChevronRight, Settings, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export default function Layout() {
  const [boards, setBoards] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { authedFetch, user, logout, isEditor } = useAuth();

  const fetchBoards = async () => {
    try {
      const data = await authedFetch('/api/boards');
      setBoards(data);
    } catch (e) {
      console.error('Failed to fetch boards:', e);
    }
  };

  useEffect(() => { 
    fetchBoards(); 
  }, [authedFetch]); // Added dependency

  useEffect(() => {
    const handler = () => fetchBoards();
    window.addEventListener('boards-changed', handler);
    return () => window.removeEventListener('boards-changed', handler);
  }, []);

  const handleCreateBoard = async () => {
    try {
      const board = await authedFetch('/api/boards', {
        method: 'POST',
        body: JSON.stringify({ name: 'New Board' }),
      });
      await fetchBoards();
      navigate(`/boards/${board.id}`);
      toast.success('Board created');
    } catch (e) {
      toast.error(e.message || 'Failed to create board');
    }
  };

  const currentBoardId = location.pathname.match(/\/boards\/([^/]+)/)?.[1];

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`no-print flex flex-col border-r bg-card transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-2 border-b px-4 py-4">
          <LayoutDashboard className="h-5 w-5 text-[hsl(var(--ring))] shrink-0" />
          {!collapsed && (
            <span className="font-heading font-semibold text-sm tracking-tight">
              RAG Tracker
            </span>
          )}
        </div>

        {/* Boards */}
        <ScrollArea className="flex-1 px-2 py-3">
          <div className="space-y-1">
            {boards.map((b) => {
              const isActive = currentBoardId === b.id;
              return (
                <Link
                  key={b.id}
                  to={`/boards/${b.id}`}
                  data-testid={`board-switch-${b.id}`}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? 'bg-secondary text-secondary-foreground font-medium'
                      : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                  }`}
                >
                  <div className="h-2 w-2 rounded-full bg-[hsl(var(--ring))] shrink-0" />
                  {!collapsed && <span className="truncate">{b.name}</span>}
                </Link>
              );
            })}
          </div>
        </ScrollArea>

        {/* Bottom actions */}
        <div className="border-t p-3 space-y-2">
          {isEditor && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2"
              onClick={handleCreateBoard}
              data-testid="board-create-button"
            >
              <Plus className="h-4 w-4" />
              {!collapsed && 'New Board'}
            </Button>
          )}

          <Link to="/settings">
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
              <Settings className="h-4 w-4" />
              {!collapsed && 'Settings'}
            </Button>
          </Link>

          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet context={useMemo(() => ({ refreshBoards: fetchBoards }), [fetchBoards])} />
      </main>
    </div>
  );
}
